import { OptionTemplate } from "shared/api/middleware-api-client";
import type { VerticalBeamPosition } from "entities/configurator/model/layoutMeta";
import { getStaanderPositionName } from "entities/configurator/model/layoutChoiceName";
import { staanderPositions } from "entities/configurator/model/staander";
import { WidthSimpleButton } from "features/option-controls/measurements-option-controls/components/WidthSimpleButton";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useId } from "react";
import { enableLayoutChanged } from "shared/store/layout/layoutStore";
import DoorOptionsIcon, { DoorOptionsType } from "shared/ui/door-options-icons";
import { LeftArrow } from "shared/ui/icons/LeftArrow";
import Heading from "shared/ui/typography/Heading";
import Text from "shared/ui/typography/Text";
import { StaanderChoiceHeader } from "../StaanderChoiceHeader";

interface EigenwijsLayoutProps {
  defaultLayout: OptionTemplate;
  updateLayout: (layout: OptionTemplate) => void;
  updateEigenwijsLayout: (eigenwijs_layout: string) => void;
  description?: string;
  title?: string;
  eigenwijs_layout?: string | null;
  staanderPosition?: VerticalBeamPosition;
  isStaanderAvailable: boolean;
  onChangeStaander: (position: VerticalBeamPosition | undefined) => void;
}

const eigenwijsLayoutData = [
  { code: "populair_1", name: "De Beurs" },
  { code: "populair_2", name: "De Cool" },
  { code: "zelfkiezen", name: "Zelf kiezen" },
];
export const EigenwijsLayout = ({
  defaultLayout,
  updateLayout,
  updateEigenwijsLayout,
  description,
  title,
  eigenwijs_layout,
  staanderPosition,
  isStaanderAvailable,
  onChangeStaander,
}: EigenwijsLayoutProps) => {
  const staanderHeadingId = useId();
  const hasStaander = staanderPosition !== undefined;
  const shouldReduceMotion = useReducedMotion();
  const handleRemoveStaander = () => onChangeStaander(undefined);
  const handlePresetChange = (code: string) => {
    enableLayoutChanged();
    updateEigenwijsLayout(code);
  };

  return (
    <div>
      <div>
        <button
          key={`layout-${defaultLayout.id}`}
          onClick={() => updateLayout(defaultLayout)}
          data-testid={`door-layout-option-${defaultLayout.code}`}
          className="gg-flex gg-items-center gg-gap-2 gg-rounded-full gg-border gg-border-[#D4D4DA] gg-bg-gray-100 gg-px-4 gg-py-2 gg-text-sm gg-tracking-wider gg-text-[#69687E] hover:gg-bg-gray-200 sm:gg-py-2.5"
        >
          <LeftArrow />
          Naar ontwerpen
        </button>

        <div className="gg-mt-4">
          <Heading headingLevel="h4" className="gg-flex">
            {title}
          </Heading>

          <Text className="gg-mb-4 gg-mt-1 gg-pr-6 gg-text-sm">
            {description}
          </Text>
        </div>

        <div className="gg-mb-2 gg-mt-4 gg-flex gg-w-full gg-flex-wrap gg-justify-center gg-gap-[1.8rem] sm:gg-items-start sm:gg-justify-start">
          {eigenwijsLayoutData.map((item) => (
            <div key={item.code}>
              <button
                type="button"
                aria-label={item.name}
                aria-pressed={item.code === eigenwijs_layout}
                onClick={() => handlePresetChange(item.code)}
              >
                <DoorOptionsIcon
                  selectedDoor={item.code as DoorOptionsType}
                  isActive={item.code === eigenwijs_layout}
                />
              </button>
              <span className="gg-block gg-pt-1 gg-text-xs gg-font-semibold gg-leading-4 gg-tracking-wide gg-text-[#292747]">
                {item.name}
              </span>
            </div>
          ))}
        </div>
        <AnimatePresence initial={false}>
          {isStaanderAvailable && (
            <motion.div
              key="staander-choices"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              className="standing-group-choice-container gg-w-full"
            >
              <StaanderChoiceHeader
                headingId={staanderHeadingId}
                showRemove={hasStaander}
                onRemove={handleRemoveStaander}
              />
              <motion.div
                layout={shouldReduceMotion ? false : "position"}
                layoutDependency={hasStaander}
                transition={{
                  layout: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
                }}
                role="group"
                aria-labelledby={staanderHeadingId}
                className="standing-group-choice-grid gg-grid gg-gap-x-2 gg-gap-y-4"
              >
                {staanderPositions.map((position) => (
                  <WidthSimpleButton
                    key={position}
                    onClick={() => onChangeStaander(position)}
                    isActive={position === staanderPosition}
                    variant="wrapping-sublayout"
                  >
                    {getStaanderPositionName(position)}
                  </WidthSimpleButton>
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
